// Bulk-import modal for the Fulfillment Entry mode.
//
// Mirrors the manufacturing pattern (ManufacturingBulkImportModal):
//   paste CSV or JSON -> Preview shows partition into valid /
//   skipped / ignored-keys -> Import button POSTs to
//   /api/fulfillment/entry/bulk.
//
// `max-h-[90vh] overflow-y-auto` per the panel-modal overflow
// convention -- the textarea + preview can get tall.

import { useMemo, useState } from "react";
import {
  buildFulfillmentBulkPreview,
  type FulfillmentBulkPreview,
} from "@/lib/fulfillment/bulkImportPreview";
import {
  createFulfillmentEntryBulk,
  type BulkEntryRow,
  type FulfillmentIntakeTemplate,
} from "@/lib/fulfillment/fulfillmentApi";

type Props = {
  templates: FulfillmentIntakeTemplate[];
  onClose: () => void;
  onImported: () => void;
};

const SAMPLE = `origin,plant_id,notes,intake_template_slug
cash_purchase,,Walk-up sale 2026-06-12,cash-intake-v1
repo_intake,,Auction lot 42,repo-intake-v1`;

function templateIdForSlug(
  slug: string | undefined,
  templates: FulfillmentIntakeTemplate[]
): string | undefined {
  if (!slug) return undefined;
  const m = templates.find((t) => t.slug === slug);
  return m?.id;
}

export function FulfillmentEntryBulkImportModal({ templates, onClose, onImported }: Props) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  const preview: FulfillmentBulkPreview = useMemo(
    () => buildFulfillmentBulkPreview(raw),
    [raw]
  );

  async function commit() {
    if (preview.kind !== "ok") return;
    setBusy(true);
    setError(null);
    setResult(null);
    const rows: BulkEntryRow[] = preview.validRows.map((r) => ({
      origin: r.origin,
      plant_id: r.plant_id,
      notes: r.notes,
      intake_template_id: templateIdForSlug(r.intake_template_slug, templates) ?? null,
    }));
    const r = await createFulfillmentEntryBulk(rows);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setResult({
      created: r.data.summary.created,
      skipped: r.data.summary.skipped.length,
    });
    onImported();
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 p-2 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-gray-900 dark:text-zinc-100">Bulk intake import</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
          Paste CSV (with header row) or a JSON array. Recognized columns: <code>origin</code>,{" "}
          <code>plant_id</code>, <code>notes</code>, <code>intake_template_slug</code>.
        </p>

        <label className="mt-3 block">
          <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Rows
          </span>
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={10}
            placeholder={SAMPLE}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 font-mono text-xs dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>

        {preview.kind === "parse-error" ? (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {preview.message}
          </div>
        ) : null}

        {preview.kind === "ok" ? (
          <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm dark:border-zinc-700 dark:bg-zinc-800">
            <div>
              Detected <strong>{preview.format.toUpperCase()}</strong> · {preview.total} rows ·{" "}
              <span className="text-green-700 dark:text-green-300">
                {preview.validRows.length} valid
              </span>{" "}
              ·{" "}
              <span className="text-amber-700 dark:text-amber-300">
                {preview.skipped.length} skipped
              </span>
            </div>
            {preview.ignoredKeys.length > 0 ? (
              <div className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                Ignored unrecognized columns: <code>{preview.ignoredKeys.join(", ")}</code>
              </div>
            ) : null}
            {preview.skipped.length > 0 ? (
              <ul className="mt-2 list-inside list-disc text-xs text-gray-700 dark:text-zinc-300">
                {preview.skipped.slice(0, 6).map((s) => (
                  <li key={s.idx}>
                    Row {s.idx + 1}: {s.reason}
                  </li>
                ))}
                {preview.skipped.length > 6 ? (
                  <li>… and {preview.skipped.length - 6} more</li>
                ) : null}
              </ul>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}
        {result ? (
          <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 dark:border-green-800 dark:bg-green-950/35 dark:text-green-100">
            Created {result.created} units · {result.skipped} skipped server-side.
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => void commit()}
            disabled={busy || preview.kind !== "ok" || preview.validRows.length === 0}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy
              ? "Importing…"
              : preview.kind === "ok"
                ? `Import ${preview.validRows.length} row${preview.validRows.length === 1 ? "" : "s"}`
                : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}
